import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import ActionLinkButton from '../../UI/ActionLinkButton/ActionLinkButton';
import useBodyScrollLock from '../../../hooks/useBodyScrollLock';
import { CATALOG_DESCRIPTIONS, CatalogDescriptionContent, CatalogDescriptionSection } from './catalogDescriptions';
import './catalogDescription.scss';

type Params = { url?: string };

// усечение по словам для превью
const truncate = (s: string, max = 220): string => {
    if (s.length <= max) return s;
    const cut = s.slice(0, max);
    const lastSpace = cut.lastIndexOf(' ');
    return (lastSpace > 0 ? cut.slice(0, lastSpace) : cut).trim() + '…';
};

const SectionBlock: React.FC<{ s: CatalogDescriptionSection }> = ({ s }) => (
    <section className="catalog-desc__section">
        {s.heading ? <h3 className="catalog-desc__section-title">{s.heading}</h3> : null}
        {s.paragraphs?.map((p, i) => (
            <p key={`p-${i}`} className="catalog-desc__paragraph">
                {p}
            </p>
        ))}
        {s.list && s.list.length > 0 ? (
            <ul className="catalog-desc__list">
                {s.list.map((item, i) => (
                    <li key={`li-${i}`} className="catalog-desc__list-item">
                        {item}
                    </li>
                ))}
            </ul>
        ) : null}
    </section>
);

const CatalogDescription: React.FC = () => {
    const { url } = useParams<Params>();
    const [open, setOpen] = useState(false);
    const dialogRef = useRef<HTMLDivElement | null>(null);
    useBodyScrollLock(open);
    const content: CatalogDescriptionContent | null = useMemo(() => (url ? (CATALOG_DESCRIPTIONS[url] ?? null) : null), [url]);
    const previewIntro = useMemo(() => (content ? truncate(content.intro, 220) : ''), [content]);
    const handleOpen = useCallback(() => setOpen(true), []);
    const handleClose = useCallback(() => setOpen(false), []);

    useEffect(() => {
        if (!open) return;
        const onKey = (e: KeyboardEvent) => e.key === 'Escape' && handleClose();
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [open, handleClose]);

    const onOverlayMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
        if (e.target === e.currentTarget) handleClose();
    };

    if (!content) return null;

    const jsonLd = {
        '@context': 'https://schema.org',
        '@type': 'Article',
        inLanguage: 'ru',
        headline: content.title,
        description: content.intro,
    };

    return (
        <article className="catalog-desc" aria-labelledby="catalog-desc-title" itemScope itemType="https://schema.org/Article">
            <h2 id="catalog-desc-title" className="catalog-desc__title" itemProp="headline">
                {content.title}
            </h2>
            <p className="catalog-desc__intro" onClick={handleOpen} itemProp="description">
                {previewIntro}
            </p>
            <div className="catalog-desc__actions">
                <ActionLinkButton text="Подробнее" onClick={handleOpen} ariaLabel="Показать полное описание" />
            </div>
            <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />

            <div
                className={`catalog-desc__overlay ${open ? 'catalog-desc__overlay--open' : ''}`.trim()}
                role="dialog"
                aria-modal="true"
                aria-labelledby="catalog-desc-dialog-title"
                onMouseDown={onOverlayMouseDown}
            >
                {open && (
                    <div ref={dialogRef} className="catalog-desc__dialog">
                        <button type="button" className="catalog-desc__dialog-close" aria-label="Закрыть" onClick={handleClose}>
                            ×
                        </button>
                        <div className="catalog-desc__content">
                            <h2 id="catalog-desc-dialog-title" className="catalog-desc__title">
                                {content.title}
                            </h2>
                            <p className="catalog-desc__paragraph">{content.intro}</p>
                            {content.sections.map((sec, idx) => (
                                <SectionBlock key={`sec-${idx}`} s={sec} />
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </article>
    );
};

export default CatalogDescription;
